import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import Select from 'react-select';
import { PostLanguageEnum, SchedulingVariantV1 } from '@/__generated__/graphql';
import { useToast } from '@/components/shadcn/use-toast';
import { Checkbox } from '@/components/shadcn/checkbox';
import { useOrganizationContext } from '@/contexts/organization-context';
import { InputGroup } from '@/components/compound-components/input-group/';
import SettingsTabTitle from '@/components/compound-components/settings/settings-tab-title';
import { timeZonesNames } from '@vvo/tzdb';
import { Badge } from '@/components/shadcn/badge';
import { Cross2Icon } from '@radix-ui/react-icons';
import { gql } from '@/__generated__';
import PostingTimes from './posting-times/posting-times';
import Text from '@/components/basic/text';
import { Switch } from '@/components/shadcn/switch';
import { SpinnerOverlay } from '@/components/basic/spinner-overlay';

type ScheduleVariantSettingsKeys =
  | 'audience'
  | 'generateComments'
  | 'preDefinedHashtags'
  | 'timezone'
  | 'postLanguage'
  | 'useEmojies'
  | 'voice';
type ScheduleVariantSettings = Pick<
  SchedulingVariantV1,
  ScheduleVariantSettingsKeys
>;

const MutationSchedulingVariantClientSettings = gql(`
  mutation SchedulingVariantV1UpdateClientSettings(
    $input: SchedulingVariantV1UpdateInput!
  ) {
    schedulingVariantV1 {
      update(input: $input) {
        ... on SchedulingVariantV1UpdateSuccess {
          result {
            id
            audience
            postLanguage
            generateComments
            preDefinedHashtags
            timezone
            useEmojies
            variant
            voice
            ...PostingTimesFragment
          }
        }
      }
    }
  }
`);

const SchedulingVariantGetByOrganization = gql(`
  query FetchVariantByOrgId(
    $input: SchedulingVariantV1GetByOrganizationInput!
  ) {
    schedulingVariantV1 {
      getByOrganization(input: $input) {
        ... on SchedulingVariantV1GetByOrganizationSuccess {
          items {
            item {
              id
              audience
              postLanguage
              generateComments
              preDefinedHashtags
              timezone
              useEmojies
              variant
              voice
              ...PostingTimesFragment
            }
          }
        }
      }
    }
  }
`);

const MutationAutoPublishSettings = gql(`
  mutation OrganizationAutoPublishSettings(
    $input: OrganiztionSettingsAutoPublishInput!
  ) {
      organization {
        settings {
        autoPublish(input: $input) {
          ... on OrganiztionSettingsAutoPublishSuccess {
           result {
            settings {
              autoPublish
            }
           }
          }
        }
      }
    }
  }
`);

const OrganizationGetByIdQuery = gql(`
  query OrganizationGetByIdAutoPublish($input: OrganizationGetByIdInput!) {
    organization {
      getById(input: $input) {
      ... on OrganizationGetByIdSuccess {
        result {
          settings {
            autoPublish
          }
        }
      }
     }
    }
  }
`);

export function PostsTab() {
  const { t } = useTranslation();
  const [updates, setUpdates] = useState<Partial<ScheduleVariantSettings>>();
  const { organizationId } = useOrganizationContext();
  const [defaultHashtags, setDefaultHashtags] = useState<string[]>([]);
  const [defaultHashtag, setDefaultHashtag] = useState('');
  const [scheduleVariantValues, setScheduleVariantValues] = useState<any>();
  const [postLanguage, setPostLanguage] = useState<{
    label: string;
    value: PostLanguageEnum;
  }>();
  const [loadingHashtags, setLoadingHashtags] = useState(false);
  const { toast } = useToast();
  const getPostLanguageLabel = (
    postLanguage: PostLanguageEnum | undefined | null
  ) => {
    switch (postLanguage) {
      case PostLanguageEnum.Es:
        return t('account-menu-settings.post-language-es');
      case PostLanguageEnum.Engb:
        return t('account-menu-settings.post-language-en-gb');
      case PostLanguageEnum.Enca:
        return t('account-menu-settings.post-language-en-ca');
      case PostLanguageEnum.En:
        return t('account-menu-settings.post-language-en');
      default:
        return t('account-menu-settings.post-language-en');
    }
  };
  const postLanguageOptionsArray = Object.keys(PostLanguageEnum).map((key) => ({
    value: PostLanguageEnum[key as keyof typeof PostLanguageEnum],
    label: getPostLanguageLabel(
      PostLanguageEnum[key as keyof typeof PostLanguageEnum]
    ),
  }));

  const { data } = useQuery(SchedulingVariantGetByOrganization, {
    skip:
      !organizationId ||
      (scheduleVariantValues &&
        scheduleVariantValues.organizationId === organizationId),
    variables: { input: { organizationId: organizationId || '' } },
  });

  const { data: organizationData } = useQuery(OrganizationGetByIdQuery, {
    variables: { input: { id: organizationId || '' } },
    skip: !organizationId,
  });

  useEffect(() => {
    if (data) {
      const schedulingVariant =
        data.schedulingVariantV1.getByOrganization.items.find(
          (itemEle) => itemEle?.item.variant === 'default'
        )?.item;

      setScheduleVariantValues(schedulingVariant);
      setDefaultHashtags(schedulingVariant?.preDefinedHashtags || []);
      if (schedulingVariant?.postLanguage) {
        setPostLanguage({
          label: getPostLanguageLabel(schedulingVariant?.postLanguage),
          value: schedulingVariant?.postLanguage,
        });
      }
    }
  }, [data, t]);

  const [updateVariant] = useMutation(MutationSchedulingVariantClientSettings, {
    onCompleted: (data) => {
      setUpdates(undefined);
      setLoadingHashtags(false);
      const result = (
        data.schedulingVariantV1.update as {
          result: Partial<SchedulingVariantV1>;
        }
      ).result;
      setScheduleVariantValues(result);

      if (result.preDefinedHashtags) {
        setDefaultHashtags(result.preDefinedHashtags);
      }
    },
    onError: (error: ApolloError) => {
      toast({
        description: error.message,
        duration: 3000,
        variant: 'destructive',
      });
    },
  });

  const [toggleAutoPublish, { loading }] = useMutation(
    MutationAutoPublishSettings,
    {
      onError: (error: ApolloError) => {
        toast({
          description: error.message,
          duration: 3000,
          variant: 'destructive',
        });
      },
      refetchQueries: ['OrganizationGetByIdAutoPublish'],
    }
  );

  const onAutoPublishChange = async (value: boolean) => {
    await toggleAutoPublish({
      variables: {
        input: {
          id: organizationId || '',
          value,
        },
      },
    });
  };

  const onSave = async (
    path?: ScheduleVariantSettingsKeys,
    value?: unknown
  ) => {
    const isImmediateChange =
      path && scheduleVariantValues?.[path]?.toString() !== value?.toString();
    if (isImmediateChange) {
      onChange(path, value);
    }

    if (isImmediateChange || updates) {
      const fullUpdates = { ...updates, [path || '']: value };
      Object.keys(fullUpdates).forEach((updateKey) => {
        const key = updateKey as ScheduleVariantSettingsKeys;
        if (
          scheduleVariantValues?.[key]?.toString() ===
          fullUpdates[key]?.toString()
        ) {
          delete fullUpdates[key];
          return updates;
        }
      });
      if (Object.keys(fullUpdates).length > 0) {
        if (fullUpdates?.preDefinedHashtags) {
          setLoadingHashtags(true);
        }
        await updateVariant({
          variables: {
            input: {
              where: {
                id: scheduleVariantValues?.id || '',
              },
              set: fullUpdates,
            },
          },
        });
      }
    }
  };

  const onChange = (path: ScheduleVariantSettingsKeys, value: unknown) =>
    setUpdates((updates) => ({ ...updates, [path]: value }));

  const handleAddHashtag = () => {
    const hashtags = defaultHashtag
      .split(' ')
      .filter((tag) => tag.trim() !== '');
    setDefaultHashtags((prevState) => [...prevState, ...hashtags]);
    setDefaultHashtag('');
  };

  const handleRemoveDefaultHashtag = (index: number) => {
    const filteredState = defaultHashtags.filter(
      (item, currentIndex) => currentIndex !== index
    );
    setDefaultHashtags(filteredState);
    onSave('preDefinedHashtags', filteredState);
  };

  if (!organizationId) {
    return (
      <>
        <SettingsTabTitle title='Posts' />
        <p className='text-gray-settings'>
          {t('account-menu-settings.org-info-not-loaded')}
        </p>
      </>
    );
  }

  if (
    organizationData?.organization?.getById?.__typename !==
    'OrganizationGetByIdSuccess'
  ) {
    return null;
  }

  const autoPublishValue =
    organizationData?.organization?.getById?.result?.settings?.autoPublish;

  return (
    <div>
      <SettingsTabTitle title='Posts' />
      <InputGroup
        className='mt-1'
        labelTitle={t('account-menu-settings.posts-audience')}
        placeholder={t('account-menu-settings.posts-audience-placeholder')}
        value={updates?.audience ?? scheduleVariantValues?.audience}
        onChange={(e) => onChange('audience', e.target.value)}
        onBlur={() => onSave()}
      />
      <div className='mb-2 mt-2'>
        <label className='mb-1 block text-gray-settings'>
          {t('account-menu-settings.time-zone')}
        </label>{' '}
        {scheduleVariantValues !== undefined ? (
          <Select
            placeholder={t('account-menu-settings.time-zone')}
            options={timeZonesNames.map((str) => ({ value: str, label: str }))}
            onChange={(timezone) => onSave('timezone', timezone?.value)}
            // TODO: Fix this to have a value instead of defaultInputValue
            defaultInputValue={
              updates?.timezone || scheduleVariantValues?.timezone || ''
            }
          />
        ) : (
          <div className='min-h-8'></div>
        )}
      </div>
      {scheduleVariantValues !== undefined && (
        <div className='mb-2 mt-2'>
          <label className='mb-1 block text-gray-settings'>
            {t('account-menu-settings.post-language')}
          </label>
          <Select
            options={postLanguageOptionsArray}
            onChange={(postLanguage) => {
              setPostLanguage({
                label: postLanguage?.label || '',
                value: postLanguage?.value || PostLanguageEnum.En,
              });
              onSave('postLanguage', postLanguage?.value);
            }}
            value={postLanguage}
          />
        </div>
      )}
      <InputGroup
        className='mt-1'
        labelTitle={t('account-menu-settings.voice-and-tone')}
        placeholder={t('account-menu-settings.voice-and-tone-placeholder')}
        value={updates?.voice || scheduleVariantValues?.voice}
        onChange={(e) => onChange('voice', e.target.value)}
        onBlur={() => onSave()}
      />
      <label className='mt-2 block text-gray-settings'>
        {t('account-menu-settings.default-hashtags')}
      </label>{' '}
      <div className='relative mb-2 flex flex-wrap gap-2'>
        {loadingHashtags && (
          <div className='absolute h-full w-full'>
            <SpinnerOverlay spinneClassName='w-6' />
          </div>
        )}
        {defaultHashtags?.map((item: string, index: number) => (
          <Badge variant={'gray'} size={'small'} key={index}>
            {item}
            {'  '}
            <Cross2Icon
              className='shrink-0 cursor-pointer'
              onClick={() => handleRemoveDefaultHashtag(index)}
            />
          </Badge>
        ))}
      </div>
      <InputGroup
        disabled={loadingHashtags}
        labelTitle=''
        className='mt-1'
        value={defaultHashtag}
        onKeyUp={(ev) => {
          if (ev.key === 'Enter') {
            const allHashtags = defaultHashtags.concat(
              defaultHashtag.split(' ').filter((tag) => tag.trim() !== '')
            );

            if (defaultHashtag?.trim()) {
              handleAddHashtag();
              onSave('preDefinedHashtags', allHashtags);
            }
          }
        }}
        onChange={(e) => {
          setDefaultHashtag(e.target.value);
        }}
        onBlur={() => {
          onSave('preDefinedHashtags', defaultHashtags);
        }}
      />
      <div className='mt-[1.8rem]'>
        <div className='mt-2 flex items-center gap-2'>
          <Checkbox
            variant={'maggie'}
            checked={updates?.useEmojies || scheduleVariantValues?.useEmojies}
            onCheckedChange={(isChecked) => onSave('useEmojies', isChecked)}
          />
          <p className='font-semibold'>
            {t('account-menu-settings.include-emojis')}
          </p>
        </div>
        <div className='mt-2 flex items-center gap-2'>
          <Checkbox
            variant={'maggie'}
            checked={
              updates?.generateComments ||
              scheduleVariantValues?.generateComments
            }
            onCheckedChange={(isChecked) =>
              onSave('generateComments', isChecked)
            }
          />
          <p className='font-semibold'>
            {t('account-menu-settings.generate-links')}
          </p>
        </div>
      </div>
      <div className='mb-4 mt-8'>
        <Text weight='medium' className='mb-2'>
          Auto publish
        </Text>
        <div className='flex items-center gap-2'>
          <Switch
            disabled={loading}
            className='cursor-pointer'
            id='auto-publish'
            checked={Boolean(autoPublishValue)}
            onClick={() => {
              onAutoPublishChange(!autoPublishValue);
            }}
          />

          {autoPublishValue ? 'On' : 'Off'}
        </div>
      </div>
      <PostingTimes scheduleVariantValues={scheduleVariantValues} />
    </div>
  );
}
